import React, { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import {
  Shield,
  AlertTriangle,
  Lock,
  Unlock,
  UserX,
  UserCheck,
  Key,
  FileWarning,
  Search,
  Filter,
  RefreshCw,
  Download,
  Eye,
  Calendar,
  Clock,
  MapPin,
  Info
} from 'lucide-react';

// GraphQL query for security logs
const GET_SECURITY_LOGS = gql`
  query GetSecurityLogs($eventType: String, $severity: String, $limit: Int) {
    securityLogs(eventType: $eventType, severity: $severity, limit: $limit) {
      id
      timestamp
      eventType
      severity
      userId
      userEmail
      ipAddress
      description
    }
  }
`;

const AdminSecurityLogs = () => {
  const [filterType, setFilterType] = useState('ALL');
  const [filterSeverity, setFilterSeverity] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);

  const { loading, error, data, refetch } = useQuery(GET_SECURITY_LOGS, {
    variables: {
      eventType: filterType !== 'ALL' ? filterType : undefined,
      severity: filterSeverity !== 'ALL' ? filterSeverity : undefined,
      limit: 100
    }
  });

  // Mock data for demonstration
  const logs = data?.securityLogs || [];

  const filteredLogs = logs.filter(log => {
    const matchesSearch = log.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.ipAddress?.includes(searchTerm);
    const matchesType = filterType === 'ALL' || log.eventType === filterType;
    const matchesSeverity = filterSeverity === 'ALL' || log.severity === filterSeverity;
    return matchesSearch && matchesType && matchesSeverity;
  });

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'info': return 'blue';
      case 'warning': return 'yellow';
      case 'high': return 'orange';
      case 'critical': return 'red';
      default: return 'gray';
    }
  };

  const getSeverityIcon = (severity) => {
    switch(severity) {
      case 'critical': return <AlertTriangle size={14} className="text-red-500" />;
      case 'high': return <FileWarning size={14} className="text-orange-500" />;
      case 'warning': return <Info size={14} className="text-yellow-500" />;
      default: return <Info size={14} className="text-blue-500" />;
    }
  };

  const getEventIcon = (eventType) => {
    switch(eventType) {
      case 'LOGIN_SUCCESS': return <UserCheck size={14} className="text-green-500" />;
      case 'LOGIN_FAILED': return <UserX size={14} className="text-red-500" />;
      case 'PERMISSION_DENIED': return <Lock size={14} className="text-orange-500" />;
      case 'PASSWORD_RESET': return <Key size={14} className="text-blue-500" />;
      case 'SUSPICIOUS_ACTIVITY': return <AlertTriangle size={14} className="text-red-500" />;
      default: return <Shield size={14} className="text-gray-500" />;
    }
  };

  const formatTimestamp = (timestamp) => {
    // Handle both ISO strings and Unix timestamps
    const date = typeof timestamp === 'string' && timestamp.length > 12 
      ? new Date(parseInt(timestamp)) 
      : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 3600000) {
      return `${Math.floor(diff / 60000)} minutes ago`;
    } else if (diff < 86400000) {
      return `${Math.floor(diff / 3600000)} hours ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const exportLogs = () => {
    const csvContent = [
      ['Timestamp', 'Event Type', 'Severity', 'User', 'IP Address', 'Description'].join(','),
      ...filteredLogs.map(log => [
        log.timestamp,
        log.eventType,
        log.severity,
        log.userEmail || 'N/A',
        log.ipAddress,
        log.description
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `security_logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Stats calculation
  const stats = {
    total: filteredLogs.length,
    critical: logs.filter(l => l.severity === 'critical').length,
    high: logs.filter(l => l.severity === 'high').length,
    warning: logs.filter(l => l.severity === 'warning').length,
    loginFailures: logs.filter(l => l.eventType === 'LOGIN_FAILED').length
  };

  const LogDetailsModal = ({ log, onClose }) => {
    if (!log) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
          <h3 className="text-lg font-semibold mb-4">Log Details</h3>
          
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Event Type</p>
                <p className="font-medium">{log.eventType}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Severity</p>
                <span className={`px-2 py-1 text-xs rounded-full bg-${getSeverityColor(log.severity)}-100 text-${getSeverityColor(log.severity)}-800`}>
                  {log.severity}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">Timestamp</p>
                <p className="font-medium">{new Date(log.timestamp).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">IP Address</p>
                <p className="font-medium">{log.ipAddress}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">User</p>
                <p className="font-medium">{log.userEmail || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">User Agent</p>
                <p className="font-medium text-xs">{log.userAgent}</p>
              </div>
            </div>
            
            <div>
              <p className="text-sm text-gray-600 mb-1">Description</p>
              <p className="bg-gray-50 p-3 rounded">{log.description}</p>
            </div>
            
            {log.metadata && Object.keys(log.metadata).length > 0 && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Additional Data</p>
                <pre className="bg-gray-50 p-3 rounded text-xs">
                  {JSON.stringify(log.metadata, null, 2)}
                </pre>
              </div>
            )}
          </div>
          
          <button
            onClick={onClose}
            className="mt-6 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Security Logs</h1>
            <p className="text-gray-600">Monitor security events and access attempts</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => refetch()}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw size={20} />
            </button>
            <button
              onClick={exportLogs}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700"
            >
              <Download size={20} />
              Export
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-gray-600">Total Events</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{stats.critical}</div>
            <div className="text-sm text-gray-600">Critical</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">{stats.high}</div>
            <div className="text-sm text-gray-600">High</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">{stats.warning}</div>
            <div className="text-sm text-gray-600">Warnings</div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{stats.loginFailures}</div>
            <div className="text-sm text-gray-600">Failed Logins</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by email, IP, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="ALL">All Events</option>
            <option value="LOGIN_SUCCESS">Login Success</option>
            <option value="LOGIN_FAILED">Login Failed</option>
            <option value="PERMISSION_DENIED">Permission Denied</option>
            <option value="PASSWORD_RESET">Password Reset</option>
            <option value="SUSPICIOUS_ACTIVITY">Suspicious Activity</option>
          </select>
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="ALL">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredLogs.map((log) => (
              <tr key={log.id} className="hover:bg-gray-50">
                <td className="px-6 py-3 whitespace-nowrap text-sm">
                  <div className="flex items-center gap-1">
                    <Clock size={12} className="text-gray-400" />
                    {formatTimestamp(log.timestamp)}
                  </div>
                </td>
                <td className="px-6 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    {getEventIcon(log.eventType)}
                    <span className="text-sm">{log.eventType}</span>
                  </div>
                </td>
                <td className="px-6 py-3 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded-full bg-${getSeverityColor(log.severity)}-100 text-${getSeverityColor(log.severity)}-800 flex items-center gap-1 w-fit`}>
                    {getSeverityIcon(log.severity)}
                    {log.severity}
                  </span>
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm">
                  {log.userEmail || 'N/A'}
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm">
                  <div className="flex items-center gap-1">
                    <MapPin size={12} className="text-gray-400" />
                    {log.ipAddress}
                  </div>
                </td>
                <td className="px-6 py-3 text-sm">
                  <div className="max-w-xs truncate">
                    {log.description}
                  </div>
                </td>
                <td className="px-6 py-3 whitespace-nowrap">
                  <button
                    onClick={() => setSelectedLog(log)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    <Eye size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredLogs.length === 0 && (
          <div className="text-center py-8">
            <Shield size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500">No security events found</p>
          </div>
        )}
      </div>

      {/* Log Details Modal */}
      {selectedLog && (
        <LogDetailsModal 
          log={selectedLog} 
          onClose={() => setSelectedLog(null)} 
        />
      )}
    </div>
  );
};

export default AdminSecurityLogs;